import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig
} from 'axios';
import { LOGIN, REFRESH } from 'src/Api/endpoints';

// Orderbook Aggregator - normalized orderbooks
export const orderBookAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_ORDERBOOK_AGGREGATOR_URL
});

// Profits WebSocket - HTTP endpoints
export const profitsWsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PROFITS_WS_URL
});

// Crypto State Manager - read-cryptos & modified-cryptos
export const cryptoStateAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_CRYPTO_STATE_URL
});

// Arbitrage Calculator (Rust) - calculation stats
export const calculatorAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_CALCULATOR_URL
});

// Prediction Manager - pair CRUD, matching, and system on/off
export const predictionManagerAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Sniper Orchestrator - the Sniper domain control plane (P1). Serves the
// `/sniper/*` CRUD + the control-plane `/match-events/*` routes that used to
// live on prediction-manager. Falls back to the prediction-manager URL so an
// env that hasn't set VITE_SNIPER_ORCHESTRATOR_URL yet still resolves a base
// (transitional — production/staging set it explicitly).
export const sniperOrchestratorAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_SNIPER_ORCHESTRATOR_URL ||
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Opener Orchestrator - the opener domain control plane (P2). Serves the
// `/api/system/opener/*` on/off + panic-lockout routes and the per-group
// `/api/system/opener/groups/*` pod lifecycle that used to live on
// prediction-manager, plus the opener group/subgroup ownership surface moved
// off the opportunities-orchestrator: `/opener-groups`, `/subgroups`,
// `/subgroup-templates`, `/groups/:id/instantiate-template/:id` and the
// pair→group / pair→subgroup assignment routes, plus the per-pair opener
// kill switches (`/pairs/:id/opener-enabled`, `/pairs/bulk-opener-enabled`,
// `/pairs/:id/hide-from-opener`, `/pairs/:id/show-in-opener`) — operating the
// opener may not require the opportunities-orchestrator to be up, not even for
// one read, since that pod scales to 0 independently of the opener domain.
// Falls back to the prediction-manager URL so an env that
// hasn't set VITE_OPENER_ORCHESTRATOR_URL yet still resolves a base
// (transitional — production/staging set it explicitly).
export const openerOrchestratorAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_OPENER_ORCHESTRATOR_URL ||
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Unwinder Orchestrator - the unwinder domain control plane (P3, mirror of the
// opener-orchestrator). Serves the `/api/system/unwinder/*` on/off +
// panic-lockout routes that used to live on prediction-manager, plus the
// per-pair unwinder kill switches (`/pairs/:id/unwinder-enabled`,
// `/pairs/bulk-unwinder-enabled`) moved off the opportunities-orchestrator for
// the same reason as their opener mirrors. Falls back to
// the prediction-manager URL so an env that hasn't set
// VITE_UNWINDER_ORCHESTRATOR_URL yet still resolves a base (transitional —
// production/staging set it explicitly).
export const unwinderOrchestratorAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_UNWINDER_ORCHESTRATOR_URL ||
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Opportunities Orchestrator - the opportunities/pair-catalog domain control
// plane (P4). Serves the mapper's pair-catalog reads (`/pairs/all`,
// `/pairs/stats*`) and `/live-events`. The group,
// subgroup and pair-assignment routes it once served now belong to the
// opener-orchestrator, as does `/pairs/categories` — the Bucket page's
// Create-group dialog reads it, so it had to move to a pod that lives when the
// opener lives — as do the per-pair opener kill switches (the unwinder
// pair toggles went to the unwinder-orchestrator); the pair-catalog WRITES and
// `/event-agent` now belong to the matching-orchestrator; the opener's own
// pair LIST is served group-scoped by position-opener (`GET /pairs`). Nothing
// on the opener's operating path reads this instance any more — it must be
// able to sit at 0 replicas while the opener runs. Falls back to the
// prediction-manager URL so an env that hasn't set
// VITE_OPPORTUNITIES_ORCHESTRATOR_URL yet still resolves a base (transitional —
// production/staging set it explicitly).
export const opportunitiesOrchestratorAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_OPPORTUNITIES_ORCHESTRATOR_URL ||
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Matching Orchestrator - the mapper's matching + schedule domain control
// plane (P4). Serves the `/matching/*` and `/schedules/*` routes that used to
// live on prediction-manager, plus the pair-catalog WRITE surface and
// `/event-agent/*` moved off the opportunities-orchestrator (which is now
// read-only for the catalog): `/pairs/:id/confirm`, `/pairs/:id/manual-confirm`,
// `/pairs/:id/reject`, `/pairs/search-markets`, `/pairs/manual-pair` and
// `PATCH /pairs/:id`. Falls back to the prediction-manager URL so an
// env that hasn't set VITE_MATCHING_ORCHESTRATOR_URL yet still resolves a base
// (transitional — production/staging set it explicitly).
export const matchingOrchestratorAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_MATCHING_ORCHESTRATOR_URL ||
    import.meta.env.VITE_PREDICTION_MANAGER_URL ||
    import.meta.env.VITE_PREDICTION_PAIR_MANAGER_URL
});

// Prediction Calculator - arbitrage calculations for prediction markets
export const predictionCalculatorAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PREDICTION_CALCULATOR_URL
});

// Prediction WebSocket - real-time prediction opportunities
export const predictionWsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PREDICTIONS_WS_URL
});

// Live-events prediction WebSocket (Phase 3 — parallel low-latency pipeline).
// Same image as predictionWsAxiosApi, different deployment + port. Falls
// back to the regular URL when the env var isn't set so dev environments
// without the live deployment still render the Live tab against the shared
// WS (just without the sub-100ms latency).
export const livePredictionWsAxiosApi = axios.create({
  baseURL:
    import.meta.env.VITE_LIVE_PREDICTIONS_WS_URL ||
    import.meta.env.VITE_PREDICTIONS_WS_URL
});

// Prediction Arbitrage Manager - positions and trade execution
export const predictionArbitrageManagerAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_PREDICTION_ARBITRAGE_MANAGER_URL
});

// Position Unwinder - unwinding rules/executions API.
// `timeout` is load-bearing here: these instances carry the rule on/off
// toggles, and a dropped/hung toggle must REJECT so the UI surfaces the
// failure and rolls back instead of hanging on the optimistic OFF state.
export const positionUnwinderAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_POSITION_UNWINDER_URL + '/api/position-unwinder',
  timeout: 30000
});

// Position Opener / Entry - entry rules/executions API (see timeout note above).
// Core base = the always-on `position-opener-node` pod. A GROUPED opener rule
// must instead be written against its owning group's feed pod at
// `${VITE_OPENER_FEED_URL}/groups/{openerGroupId}/api/position-opener` — see
// `Modules/Predictions/api/openerEndpoint.ts` (`resolveOpenerBase`). This
// instance stays the core/ungrouped base and the read/bulk surface.
export const positionOpenerAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_POSITION_OPENER_URL + '/api/position-opener',
  timeout: 30000
});

// Notifications WebSocket service - paginated history feed
export const notificationsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_NOTIFICATIONS_WS_URL
});

// Trade360 fixture-binding control plane (`/match-external-refs/*`) — the
// External Live Signals framework's bind/suggest/confirm/acting/events
// routes. Same POD as `positionOpenerAxiosApi` (`position-opener`), but these
// routes sit at the pod's ROOT rather than under `/api/position-opener`, so
// this is a separate instance sharing the bare `VITE_POSITION_OPENER_URL`.
export const matchExternalRefsAxiosApi = axios.create({
  baseURL: import.meta.env.VITE_POSITION_OPENER_URL,
  timeout: 30000
});

const ALL_APIS = [
  orderBookAxiosApi,
  profitsWsAxiosApi,
  cryptoStateAxiosApi,
  calculatorAxiosApi,
  predictionManagerAxiosApi,
  sniperOrchestratorAxiosApi,
  openerOrchestratorAxiosApi,
  unwinderOrchestratorAxiosApi,
  opportunitiesOrchestratorAxiosApi,
  matchingOrchestratorAxiosApi,
  predictionCalculatorAxiosApi,
  predictionWsAxiosApi,
  livePredictionWsAxiosApi,
  predictionArbitrageManagerAxiosApi,
  positionUnwinderAxiosApi,
  positionOpenerAxiosApi,
  notificationsAxiosApi,
  matchExternalRefsAxiosApi
];

// Response interceptor: on 401, try a silent /auth/refresh once, then
// replay the original request with the new token. If refresh itself
// fails (e.g. session max-age reached), let the 401 propagate so the
// AuthProvider falls back to the login screen.
type RetryableConfig = AxiosRequestConfig & { _authRetry?: boolean };

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return url.endsWith(LOGIN) || url.endsWith(REFRESH);
}

/**
 * Attach the shared Bearer-token request interceptor and the 401-refresh
 * response interceptor to an axios instance. Extracted so per-group opener
 * instances (built on the fly by `resolveOpenerAxios`) get the SAME auth
 * behaviour as the module-level singletons — a grouped activation routed to a
 * feed pod must still carry the bearer and survive a silent token refresh.
 */
export function attachAuthInterceptors(api: AxiosInstance): AxiosInstance {
  api.interceptors.request.use(
    async config => {
      const token = _getToken();
      if (token) {
        config.headers = {
          ...config.headers,
          authorization: `Bearer ${token}`,
          Accept: 'application/json'
        };
      }
      return config;
    },
    error => Promise.reject(error)
  );

  api.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      const status = error.response?.status;

      if (
        status !== 401 ||
        !config ||
        config._authRetry ||
        isAuthEndpoint(config.url)
      ) {
        return Promise.reject(error);
      }

      // Lazy-load to break a potential circular import at module init.
      const { refreshAuthToken } = await import(
        'src/Common/context/AuthContext'
      );
      const newToken = await refreshAuthToken();
      if (!newToken) return Promise.reject(error);

      config._authRetry = true;
      config.headers = {
        ...config.headers,
        authorization: `Bearer ${newToken}`
      };
      return api.request(config);
    }
  );

  return api;
}

ALL_APIS.forEach(attachAuthInterceptors);

/**
 * Per-base opener axios instances, cached by `baseURL`. A grouped opener rule
 * activates against its owning group's feed pod (a base URL that varies per
 * group), so we can't use a single module-level instance. Each base gets ONE
 * instance — built with the same timeout + auth interceptors as the core
 * `positionOpenerAxiosApi` — reused across calls. The core base resolves to
 * the existing `positionOpenerAxiosApi` so ungrouped writes are unchanged.
 */
const openerAxiosByBase = new Map<string, AxiosInstance>();

export function resolveOpenerAxios(baseURL: string): AxiosInstance {
  // The core/ungrouped base maps to the shared singleton. Compare against both
  // the instance's own (import-time) baseURL AND the freshly env-derived core
  // base, so a runtime env change still resolves to the singleton. In a real
  // build the two are identical (Vite inlines import.meta.env); they only
  // diverge under test env-stubbing after module load.
  const coreBase = positionOpenerAxiosApi.defaults.baseURL;
  const envCoreBase =
    import.meta.env.VITE_POSITION_OPENER_URL + '/api/position-opener';
  if (baseURL === coreBase || baseURL === envCoreBase) {
    return positionOpenerAxiosApi;
  }

  const cached = openerAxiosByBase.get(baseURL);
  if (cached) return cached;

  const instance = attachAuthInterceptors(
    axios.create({ baseURL, timeout: 30000 })
  );
  openerAxiosByBase.set(baseURL, instance);
  return instance;
}

export function _getToken(): string {
  return localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY!) || '';
}
